import React, { useState } from 'react';
import { PieChart, Download, Info } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения детализации затрат по AR-проекту
 */
const CostBreakdown = ({ breakdown, results, showFullBreakdown, setShowFullBreakdown }) => {
  const [activeTab, setActiveTab] = useState('table');
  
  if (!breakdown || breakdown.length === 0 || !results) {
    return null;
  }

  // Получение названия категории для отображения
  const getCategoryDisplayName = (category) => {
    const categoryMap = {
      development: 'Разработка',
      design: 'Дизайн',
      content: 'Контент и 3D-модели',
      backend: 'Бэкенд и серверная часть',
      localization: 'Локализация',
      maintenance: 'Обслуживание и поддержка'
    };
    return categoryMap[category] || category;
  };

  // Создание данных для диаграммы
  const chartData = breakdown.map(item => ({
    ...item,
    name: getCategoryDisplayName(item.category)
  }));

  // Получение цвета для категории
  const getCategoryColor = (category) => {
    const colorMap = {
      development: 'bg-indigo-500 dark:bg-indigo-400',
      design: 'bg-pink-500 dark:bg-pink-400',
      content: 'bg-amber-500 dark:bg-amber-400',
      backend: 'bg-purple-500 dark:bg-purple-400',
      localization: 'bg-teal-500 dark:bg-teal-400',
      maintenance: 'bg-blue-500 dark:bg-blue-400'
    };
    return colorMap[category] || 'bg-gray-500 dark:bg-gray-400';
  };

  // Получение текстового цвета для категории
  const getCategoryTextColor = (category) => {
    const colorMap = {
      development: 'text-indigo-600 dark:text-indigo-300',
      design: 'text-pink-600 dark:text-pink-300',
      content: 'text-amber-600 dark:text-amber-300',
      backend: 'text-purple-600 dark:text-purple-300',
      localization: 'text-teal-600 dark:text-teal-300',
      maintenance: 'text-blue-600 dark:text-blue-300'
    };
    return colorMap[category] || 'text-gray-600 dark:text-gray-300';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-indigo-500" />
          Детализация затрат
        </h3>
      </div>

      {/* Вкладки для разных типов отображения */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('table')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'table'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Таблица затрат
        </button>
        <button
          onClick={() => setActiveTab('bars')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'bars'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Диаграмма
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>Распределение затрат по категориям. Это помогает понять структуру расходов и спланировать бюджет проекта.</p>
        </div>
      </div>

      {/* Табличное представление */}
      {activeTab === 'table' && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[500px] cost-breakdown-table">
          <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[500px]">
            <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
              <tr>
                <th scope="col" className="px-3 py-2 font-semibold">Категория</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">Стоимость</th>
                <th scope="col" className="px-3 py-2 font-semibold text-right">% от общей</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {breakdown.map((item) => (
                <tr
                  key={item.category}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
                >
                  <td className="px-3 py-2 whitespace-nowrap font-medium">
                    <span className={`inline-block w-3 h-3 rounded-full mr-2 ${getCategoryColor(item.category)}`}></span>
                    {getCategoryDisplayName(item.category)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right font-medium">
                    ${formatNumber(item.cost, 0)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {formatNumber(item.percentage, 1)}%
                  </td>
                </tr>
              ))}
              {/* Итоговая строка */}
              <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
                <td className="px-3 py-2 whitespace-nowrap text-right" colSpan="1">Итого:</td>
                <td className="px-3 py-2 whitespace-nowrap text-right">${formatNumber(results.totalCost, 0)}</td>
                <td className="px-3 py-2 whitespace-nowrap text-right">100%</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Представление с диаграммой */}
      {activeTab === 'bars' && (
        <div className="space-y-3 mt-2">
          {chartData.map((item) => (
            <div key={item.category} className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">{item.name}</span>
                <span className="text-sm font-medium">{formatNumber(item.percentage, 1)}%</span>
              </div>
              <div className="h-6 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${getCategoryColor(item.category)} rounded-full`} 
                  style={{ width: `${item.percentage}%` }}
                ></div>
              </div>
              <div className="text-right text-xs font-medium">
                <span className={getCategoryTextColor(item.category)}>
                  ${formatNumber(item.cost, 0)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CostBreakdown;